import { Injectable, computed, inject, signal } from '@angular/core';
import { UrlService } from '@eforall/common';
import { BREAKPOINT_COMPUTER, BREAKPOINT_TABLET, BREAKPOINT_WIDE_COMPUTER } from './mobile-layout-constants';
import { MobileLayoutDimensions } from './mobile-layout-dimensions';


@Injectable({ providedIn: 'root' })
export class MobileLayoutService {

	private readonly urlService = inject(UrlService);
	public readonly isIOS = computed(() => { return this.urlService.isIOS() });
	public readonly isSafari = computed(() => { return this.urlService.isSafari() });

	private _deviceSize = signal({ width: window.innerWidth, height: window.innerHeight });
	private _viewportSize = signal({ width: window.innerWidth, height: window.innerHeight });

	/**
	 * The height of the mobile device, as returned by window.innerHeight.
	 */
	public readonly deviceHeight = this._deviceSize.asReadonly();

	/**
	 * The height of the viewport, as returned by the visualViewport API.
	 */
	public readonly viewportHeight = this._viewportSize.asReadonly();


	public readonly keyboardHeight = computed(() => {

		const deviceHeight = this._deviceSize().height;
		const viewportHeight = this._viewportSize().height;

		// The difference between them is the keyboard's height.
		// We use a threshold (e.g., 150px) to be certain it's a keyboard
		// and not just a browser UI change like the address bar shrinking.
		const keyboardHeight = deviceHeight - viewportHeight;
		const KEYBOARD_THRESHOLD = 150;

		return keyboardHeight >= KEYBOARD_THRESHOLD ? keyboardHeight : 0;

	});


	/**
	 * An ESTIMATE of whether the keyboard is currently visible.  This
	 * will be pretty accurate on Android devices.  With Apple devices
	 * we need to add a fudge factor, and hope for the best. Eventually
	 * this can be replaced with the virtualKeyboard API but, alas,
	 * Apple does not support it currently.
	 * 
	 * https://caniuse.com/?search=virtualKeyboard
	 */
	public readonly keyboardIsVisible = computed(() => {
		return this.keyboardHeight() > 0;
	});


	/**
	 * Automatically hide the bottom navigation bar if the viewport is
	 * too short and it would take up too much of the real estate.
	 */
	public readonly hideNavBar = computed(() => {
		return this._viewportSize().height < 500;
	});


	private setHideNavbarClass() {
		const hideNavBar = this.hideNavBar();
		const body = document.getElementsByTagName('body')[0];
		if (hideNavBar) body?.classList.add('hide-navbar');
		else body?.classList.remove('hide-navbar');
	}


	/**
	 * Defines where the navbar is positioned.  The value is
	 * non-zero on iOS devices because the keyboard floats.
	 * 
	 * The advisor positions the prompt input field relative
	 * to this.
	 */
	public readonly navbarBottomOffset = computed(() => {
		if (this.keyboardIsVisible()) {

			if (this.isIOS()) {
				//
				// This is a hack for iOS according to Gemini for a "safe area".
				// It is supposedly a Noop on Android.
				//
				const bottomSafeArea = parseFloat(getComputedStyle(document.documentElement)
					.getPropertyValue('padding-bottom'));

				return Math.max(this._deviceSize().height - this._viewportSize().height - bottomSafeArea, 0);
			}
			else {
				return 0;
			}
		}
		else {
			return 0;
		}
	});


	public readonly dimensions = computed<MobileLayoutDimensions>(() => {
		return this.calcDimensions();
	});


	constructor(
	) {

		if (window.visualViewport) {

			const viewport = window.visualViewport;

			viewport.addEventListener('resize', () => {

				this._viewportSize.set({ width: viewport.width, height: viewport.height });
				this._deviceSize.set({ width: window.innerWidth, height: window.innerHeight });
				this.setHideNavbarClass();
			});
		}
		else {
			window.addEventListener('resize', () => {
				this._viewportSize.set({ width: window.innerWidth, height: window.innerHeight });
				this._deviceSize.set({ width: window.innerWidth, height: window.innerHeight });
				this.setHideNavbarClass();
			});
		}

		window.addEventListener('scroll', () => {
			if (window.scrollY > 0) {
				window.scrollTo({ left: 0, top: 0, behavior: 'instant' });
			}
		}, {passive:true})
	}


	/**
	 * Calculate all of the layout sizes.
	 * This is called each time the window is resized.
	 */
	private calcDimensions(): MobileLayoutDimensions {

		const viewport = this._viewportSize();

		const device = this._deviceSize();
		const w = device.width;
		const h = device.height;

		//
		// Determine what breakpoint range the window width equates to
		//
		let breakpoint: 'phone' | 'tablet' | 'computer' | 'wide-computer' = 'phone';

		if (w >= BREAKPOINT_WIDE_COMPUTER) breakpoint = 'wide-computer';
		else if (w >= BREAKPOINT_COMPUTER) breakpoint = 'computer';
		else if (w > BREAKPOINT_TABLET) breakpoint = 'tablet';
		else breakpoint = 'phone';

		const d: MobileLayoutDimensions = {
			breakpoint,
			screenWidth: window.outerWidth,
			screenHeight: window.outerHeight,
			windowWidth: w,
			windowHeight: h,
			contentHeight: viewport.height,
			contentWidth: viewport.width,
		};


		if (breakpoint != 'phone') {
			d.contentHeight = h - 36; //Padding added
		}

		document.documentElement.style.setProperty('--content-height', `${d.contentHeight}px`);
		return d;
	}
}